import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { getUserByIdFx } from '@nx-frontend/shared/store/users-store';
import { ManageMembersView, OrgMemberRole, Permissions } from '@nx-frontend/shared/types';
import { getNodeNameByType, getPermission } from '@nx-frontend/shared/utils';
import {
  $context,
  $manageMembersWorkspaceMemberUserIds,
  $menuItem,
  $portalClientAccess,
  $portalConsoleAccess,
  $sortedByRoleMembers,
  updateMenuItemEvent,
  updatePermissionsFx,
  updateViewModeEvent,
} from '../store';
import {
  getPortalClientOrgRolesFromAccess,
  getPortalConsoleOrgRolesFromAccess,
  getUpdatedPermissions,
  userHasWorkspaceAccess,
} from '../utils';
import OrgAccessItem, { AccessItems } from './org-access-item';

export function PortalNodeList({
  onUpdateMenuItemPermissions,
}: {
  onUpdateMenuItemPermissions?: (id: string, permissions: Permissions) => void;
}) {
  const t = useTranslations('features');
  const [updateViewMode, updateMenuItem, updatePermissions, getUserById] = useEvent([
    updateViewModeEvent,
    updateMenuItemEvent,
    updatePermissionsFx,
    getUserByIdFx,
  ]);
  const [members, menuItem, portalConsoleAccess, portalClientAccess, context, workspaceMemberUserIds] = useUnit([
    $sortedByRoleMembers,
    $menuItem,
    $portalConsoleAccess,
    $portalClientAccess,
    $context,
    $manageMembersWorkspaceMemberUserIds,
  ]);
  const [tempWorkspaceMemberUserIds, setTempWorkspaceMemberUserIds] = useState<number[]>([]);
  const [draftIds, setDraftIds] = useState<number[]>([]);

  const { toast } = useToast();

  const [permissions, setPermissions] = useState<Permissions | undefined>(menuItem?.permissions);
  const availablePortalOrgRoles = getPortalClientOrgRolesFromAccess(portalClientAccess);
  const availableConsoleOrgAccessRoles = getPortalConsoleOrgRolesFromAccess(portalConsoleAccess);

  const preparedItems = useMemo(() => {
    if (permissions) {
      return members.reduce((acc, member) => {
        if (member.role === OrgMemberRole.GUEST && !workspaceMemberUserIds.includes(member.userId)) {
          return acc;
        }
        const hasWorkspaceAccess = userHasWorkspaceAccess(member.userId, [
          ...workspaceMemberUserIds,
          ...tempWorkspaceMemberUserIds,
        ]);
        const clientRoleAccess = availablePortalOrgRoles.includes(member.role);
        const consoleRoleAccess = availableConsoleOrgAccessRoles.includes(member.role) && hasWorkspaceAccess;
        const hasAccess =
          getPermission({ userId: member.userId, permissions }) && clientRoleAccess && hasWorkspaceAccess;
        const initialAccessValue = hasAccess ? AccessItems.ACCESS_GRANTED : AccessItems.NO_ACCESS;
        acc.push({
          userId: member.userId,
          initialAccessValue,
          accessValue: initialAccessValue,
          changeDisabled: !clientRoleAccess || consoleRoleAccess,
          role: member.role,
          highlightedRole: consoleRoleAccess && hasWorkspaceAccess,
          roleRestricted: !clientRoleAccess,
        });
        return acc;
      }, [] as { userId: number; initialAccessValue: AccessItems; accessValue: AccessItems; changeDisabled: boolean; role: OrgMemberRole; roleRestricted: boolean; highlightedRole: boolean }[]);
    }
    return [];
  }, [
    availableConsoleOrgAccessRoles,
    availablePortalOrgRoles,
    members,
    permissions,
    tempWorkspaceMemberUserIds,
    workspaceMemberUserIds,
  ]);

  if (!permissions) {
    return;
  }
  const handleChange = ({ userId, accessValue }: { userId: number; accessValue: AccessItems }, value: AccessItems) => {
    if (accessValue === value) {
      return;
    }

    const newPermissions = getUpdatedPermissions(
      permissions,
      preparedItems
        .filter(
          (item) =>
            !item.roleRestricted && (workspaceMemberUserIds.includes(item.userId) || draftIds.includes(item.userId))
        )
        .map((item) => item.userId),
      userId,
      value === AccessItems.ACCESS_GRANTED
    );
    if (value) {
      setTempWorkspaceMemberUserIds([...tempWorkspaceMemberUserIds, userId]);
    }
    setPermissions(newPermissions);
    if (value) {
      if (!draftIds.includes(userId)) {
        setDraftIds([...draftIds, userId]);
      }
    } else {
      setDraftIds(draftIds.filter((id) => id !== userId));
    }
  };
  const handleSubmit = async () => {
    if (menuItem && menuItem.permissions) {
      const initialPermission = { ...menuItem.permissions };
      updateMenuItem({ ...menuItem, permissions });
      if (onUpdateMenuItemPermissions) {
        onUpdateMenuItemPermissions(menuItem.id, permissions);
      }
      const nonWorkspaceMemberIds = permissions.allowUserIds.filter(
        (userId) => !workspaceMemberUserIds.includes(userId)
      );
      const nonWorkspaceUsers = await Promise.all(
        nonWorkspaceMemberIds.map((id) => {
          return getUserById({ id });
        })
      );
      const invites = nonWorkspaceUsers.map((user) => {
        return {
          email: user.email,
          role: 'editor',
        };
      });
      goToNodeAccessList();
      updatePermissions({
        permissions,
        targetId: menuItem.id,
        invites,
        workspaceId: context.workspaceId,
        portalUrl: context.portalUrl,
      })
        .then(() => {
          const nodeName = menuItem ? getNodeNameByType(menuItem.type) : '';
          toast({
            title: t(`manage-members.change_access_settings_for_${nodeName ? nodeName : 'item'}`, {
              menuItemName: menuItem.name,
            }),
          });
        })
        .catch(() => {
          updateMenuItem({ ...menuItem, permissions: initialPermission });
          if (onUpdateMenuItemPermissions) {
            onUpdateMenuItemPermissions(menuItem.id, initialPermission);
          }
          toast({
            title: t('manage-members.an_error_changing_permissions'),
          });
        });
    }
  };

  const goToNodeAccessList = () => {
    updateViewMode(ManageMembersView.NODE_ACCESS);
  };

  return (
    <>
      <div className="w-full min-h-0 h-full overflow-y-auto mt-3">
        {preparedItems.map((item, index) => (
          <OrgAccessItem
            key={item.userId}
            userId={item.userId}
            initialAccessValue={item.initialAccessValue}
            accessValue={item.accessValue}
            onChange={(value) => handleChange(item, value)}
            isLast={members.length - 1 === index}
            role={item.role}
            roleRestricted={item.roleRestricted}
            changeDisabled={item.changeDisabled}
            highlightedRole={item.highlightedRole}
            portalId={context.portalId}
          />
        ))}
      </div>
      <div className="flex mt-3 justify-end gap-4">
        <Button type={ButtonType.STROKED} size={ButtonSize.XL} onClick={goToNodeAccessList}>
          {t('manage-members.cancel')}
        </Button>
        <Button type={ButtonType.FLAT} size={ButtonSize.XL} className="min-w-[172px]" onClick={handleSubmit}>
          {t('manage-members.save_changes')}
        </Button>
      </div>
    </>
  );
}

export default PortalNodeList;
